import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { listMeasurements, FIELD_META } from "@/services/measurements";
import { Clock, Camera, Edit3, Loader2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Contenido de la card colapsable "Historial de mediciones".
 * Se monta solo cuando la card se abre (lazy load).
 */
export function MeasurementHistoryContent({ isOpen }) {
  const { user } = useAuth();
  const { isDemo, isImpersonating, impersonatedUser } = useUserProfile();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);

  const targetUid = isImpersonating ? impersonatedUser?.uid : user?.uid;

  useEffect(() => {
    if (!isOpen) return;
    if (!targetUid || isDemo) {
      setItems([]);
      return;
    }
    if (items !== null) return; // already loaded
    (async () => {
      try {
        const list = await listMeasurements(targetUid, 10);
        setItems(list);
      } catch (e) {
        setErr(e.message || "Error al cargar historial");
        setItems([]);
      }
    })();
  }, [isOpen, targetUid, isDemo, items]);

  if (!isOpen) return null;

  if (items === null) {
    return (
      <div className="py-3 flex items-center justify-center gap-2" data-testid="history-loading">
        <Loader2 size={14} className="animate-spin" color="#71644C" />
        <span className="text-xs" style={{ color: "#71644C" }}>Cargando…</span>
      </div>
    );
  }

  if (err) {
    return (
      <p className="text-xs py-2" style={{ color: "#C0453A" }} data-testid="history-error">
        {err}
      </p>
    );
  }

  if (isDemo) {
    return (
      <p className="text-xs py-2" style={{ color: "#71644C" }} data-testid="history-demo">
        En modo demo no se muestra historial. Inicia sesión con una cuenta real para registrar y ver mediciones.
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-xs py-2" style={{ color: "#71644C" }} data-testid="history-empty">
        Aún no hay mediciones de seguimiento registradas. Usa el botón "Nueva medición" para empezar.
      </p>
    );
  }

  return (
    <div className="space-y-2 mt-2" data-testid="history-list">
      {items.map((m) => (
        <MeasurementRow key={m.id} m={m} />
      ))}
    </div>
  );
}

function MeasurementRow({ m }) {
  const [expanded, setExpanded] = useState(false);
  const isCam = m.method === "camera";
  const fields = m.fields || {};
  const entries = Object.entries(fields);

  // Parse date safely, supporting Firestore Timestamps, ISO strings, and numbers
  let dateObj = null;
  if (m.createdAt) {
    if (typeof m.createdAt.toDate === "function") {
      dateObj = m.createdAt.toDate();
    } else if (m.createdAt.seconds) {
      dateObj = new Date(m.createdAt.seconds * 1000);
    } else {
      dateObj = new Date(m.createdAt);
    }
  }

  const isValidDate = dateObj && !isNaN(dateObj.getTime());
  const dateStr = isValidDate
    ? dateObj.toLocaleString("es-CO", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  // Safe value renderer to prevent React "Objects are not valid as a React child" crashes
  const renderValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") {
      if (typeof v.toDate === "function") {
        return v.toDate().toLocaleString("es-CO");
      }
      return JSON.stringify(v);
    }
    return String(v);
  };

  // Resumen prioritario: PA, glicemia, peso, FC
  const priority = ["paSistolica", "paDiastolica", "glucemia", "peso", "heartRate"];
  const summary = [];
  if (fields.paSistolica && fields.paDiastolica) {
    summary.push(`PA ${fields.paSistolica}/${fields.paDiastolica}`);
  }
  if (fields.glucemia) summary.push(`Glu ${fields.glucemia}`);
  if (fields.peso) summary.push(`${fields.peso}kg`);
  if (fields.heartRate) summary.push(`${fields.heartRate}lpm`);

  // Campos extra (no prioritarios, con valor)
  const extraEntries = entries.filter(
    ([k, v]) => !priority.includes(k) && v !== null && v !== undefined && v !== ""
  );

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: isCam ? "#B2DFDB" : "#E2D9CB",
        background: isCam ? "#F0FDFA" : "#FAFAFA",
      }}
      data-testid={`history-row-${m.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
          style={{
            background: isCam ? "#CCFBF1" : "#E2D9CB",
            color: isCam ? "#0F766E" : "#71644C",
          }}
        >
          {isCam ? <Camera size={10} /> : <Edit3 size={10} />}
          {isCam ? "Cámara" : "Manual"}
        </span>
        <span
          className="text-[11px] flex items-center gap-1"
          style={{ color: "#71644C" }}
        >
          <Clock size={10} /> {dateStr}
        </span>
      </div>

      {/* Resumen principal */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {summary.map((s, i) => (
          <span key={i} className="text-xs font-semibold" style={{ color: "#2D2A24" }}>
            {s}
          </span>
        ))}
        {summary.length === 0 &&
          entries.slice(0, 3).map(([k, v]) => (
            <span key={k} className="text-xs" style={{ color: "#2D2A24" }}>
              <span className="font-medium">{FIELD_META[k]?.label || k}:</span>{" "}
              {renderValue(v)}{FIELD_META[k]?.unit ? ` ${FIELD_META[k].unit}` : ""}
            </span>
          ))}
        {extraEntries.length > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
            className="flex items-center gap-0.5 text-[11px] font-semibold cursor-pointer"
            style={{ color: "#0F766E", background: "none", border: "none", padding: 0 }}
            data-testid={`history-expand-${m.id}`}
          >
            {expanded
              ? <><ChevronUp size={11} /> Ver menos</>
              : <><ChevronDown size={11} /> +{extraEntries.length} más</>
            }
          </button>
        )}
      </div>

      {/* Detalle expandido */}
      {expanded && extraEntries.length > 0 && (
        <div
          className="mt-2 pt-2 space-y-1.5 border-t"
          style={{ borderColor: isCam ? "#B2DFDB" : "#E2D9CB" }}
          data-testid={`history-detail-${m.id}`}
        >
          {extraEntries.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-3 text-[11px]">
              <span style={{ color: "#71644C" }}>{FIELD_META[k]?.label || k}</span>
              <span className="font-semibold shrink-0 text-right" style={{ color: "#2D2A24" }}>
                {renderValue(v)}{FIELD_META[k]?.unit ? ` ${FIELD_META[k].unit}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {m.note && (
        <p className="text-[11px] mt-1 italic" style={{ color: "#71644C" }}>
          "{m.note}"
        </p>
      )}
    </div>
  );
}

